import { useState, useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import axiosInstance from '../utils/axiosInstance';
import { Box, TextField, Button, Stack, Autocomplete } from '@mui/material';
import HowToReg from '@mui/icons-material/HowToReg';
import ClientModal from './ClientModal';
import { formatPaymentMonth } from '../utils/format';

const OrderClientInfo = ({
    hasLimit,
    isEditMode = true,
    register,
    control,
    setValue,
    errors,
    fieldErrors = {},
    setFieldErrors,
    setSelectedClient,
    mode = 'order',
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [contactOptions, setContactOptions] = useState([]);
    const [paymentOptions, setPaymentOptions] = useState([]);
    const [billingRules, setBillingRules] = useState([]);
    //const [defaultPaymentTerms, setDefaultPaymentTerms] = useState(null);
    const clientId = useWatch({ control, name: 'client_id' });
    useEffect(() => {
        const fetchPaymentMethods = async () => {
            try {
                const res = await axiosInstance.get('/list/payment-methods', { withCredentials: false });
                setPaymentOptions(res.data || []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchPaymentMethods();
    }, []);

    // オートコンプリート
    useEffect(() => {
        if (!clientId) {
            setContactOptions([]);
            setBillingRules([]);
            return;
        }

        const run = async () => {
            try {
                const [contactRes, billingRes] = await Promise.all([
                    axiosInstance.get(`/client/${clientId}`),
                    axiosInstance.get(`/client/${clientId}/billing-rules`)
                ]);

                setContactOptions(contactRes.data.items || []);
                setBillingRules(
                    Array.isArray(billingRes.data.items)
                        ? billingRes.data.items
                        : []
                );
            } catch (err) {
                if (err.response?.status === 404) {
                    setValue('client_contact_id', null);
                    return;
                }
                console.error(err);
            }
        };

        run();
    }, [clientId]);
    const billingRuleOptions = Array.from(
        new Set([
            ...(billingRules || []).map(r =>
                `${r.closing_day}日締め${formatPaymentMonth(r.payment_month_offset)}${r.payment_day}日支払い`
            ),
            //defaultPaymentTerms
        ])
    ).filter(Boolean);
    const handleCustomerSelect = (client) => {
        setIsModalOpen(false);
        setValue('client_id', client.client_id);
        setValue('client_name', client.client_name);
        setValue('branch_name', client.branch_name);
        setValue('client_phone', client.client_phone);
        //setValue('payment_terms', client.payment_terms);
        //setValue('payment_method_id', client.payment_method_id);
        //setValue('payment_method_name', client.payment_method_name);
        setValue('client_contact_id', null);
        setValue('client_contact_name', '');
        setValue('client_contact_phone', '');
        setValue('client_contact_email', '');
        setFieldErrors?.({ ...fieldErrors, client_name: '', client_contact_name: '' });
        setSelectedClient?.(client);
        //setDefaultPaymentTerms(client.payment_terms);
    };
    return (
        <>
            <Stack direction="row" spacing={1} mb={2} mt={1} alignItems="start">
                <TextField
                    label="会社名"
                    disabled={!isEditMode}
                    inputProps={{ maxLength: 255 }}
                    {...register('client_name', {
                        required: '※会社名は必須です',
                        maxLength: {
                            value: 255,
                            message: '※255文字以内で入力してください'
                        },
                        onChange: () => {
                            setValue('client_id', null, { shouldDirty: true });
                            setValue('client_contact_id', null);
                            setValue('shipping_address_id', null);
                        }
                    })}
                    error={!!errors.client_name}
                    helperText={errors.client_name?.message}
                    multiline
                />
                {mode !== 'invoice' && (
                    <Button
                        variant="outlined"
                        sx={{ minWidth: 130 }}
                        startIcon={<HowToReg />}
                        onClick={() => setIsModalOpen(true)}
                        disabled={!hasLimit('max_clients') || !isEditMode}
                    >
                        顧客を選択
                    </Button>
                )}
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', rowGap: 2, columnGap: 1, }}>
                <TextField
                    label="事業所名"
                    disabled={!isEditMode}
                    inputProps={{ maxLength: 255 }}
                    {...register('branch_name', {
                        maxLength: {
                            value: 255,
                            message: '※255文字以内で入力してください'
                        }
                    })}
                    error={!!errors.branch_name}
                    helperText={errors.branch_name?.message}
                    multiline
                />
                <TextField
                    label="電話番号"
                    disabled={!isEditMode}
                    inputProps={{ maxLength: 20 }}
                    {...register('client_phone', {
                        pattern: {
                            value: /^(?:\+?\d{1,4}[-\s]?)?0?\d{1,4}[-\s]?\d{1,4}[-\s]?\d{4}$/,
                            message: '※数字とハイフンのみ使用できます'
                        },
                        maxLength: {
                            value: 20,
                            message: '※20文字以内で入力してください'
                        }
                    })}
                    error={!!errors.client_phone}
                    helperText={errors.client_phone?.message}
                />
                {mode !== 'invoice' && (
                    <>
                        <Controller
                            name="client_contact_name"
                            control={control}
                            rules={{
                                maxLength: {
                                    value: 100,
                                    message: '※100文字以内で入力してください'
                                }
                            }}
                            render={({ field, fieldState }) => (
                                <Autocomplete
                                    {...field}
                                    freeSolo
                                    openOnFocus
                                    options={contactOptions}
                                    getOptionLabel={(option) =>
                                        typeof option === 'string' ? option : option.client_contact_name
                                    }
                                    value={
                                        contactOptions.find(c => c.client_contact_name === field.value) || field.value || ''
                                    }
                                    onChange={(e, newValue) => {
                                        const value =
                                            typeof newValue === 'string'
                                                ? newValue
                                                : newValue?.client_contact_name || '';

                                        field.onChange(value);

                                        if (typeof newValue === 'string') {
                                            setValue('client_contact_id', null);
                                            setValue('client_contact_phone', '');
                                            setValue('client_contact_email', '');
                                        } else if (newValue) {
                                            setValue('client_contact_id', newValue.client_contact_id);
                                            setValue('client_contact_name', newValue.client_contact_name ?? '');
                                            setValue('client_contact_phone', newValue.client_contact_phone ?? '');
                                            setValue('client_contact_email', newValue.client_contact_email ?? '');
                                            //setValue('recipient_name', newValue.client_contact_name ?? '');
                                        }
                                    }}
                                    onInputChange={(e, value) => {
                                        field.onChange(value);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="担当者名"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            inputProps={{
                                                ...params.inputProps,
                                                maxLength: 100
                                            }}
                                            multiline
                                        />
                                    )}
                                    disabled={!isEditMode}
                                />
                            )}
                        />

                        <TextField
                            label="担当者TEL"
                            disabled={!isEditMode}
                            inputProps={{ maxLength: 20 }}
                            {...register('client_contact_phone', {
                                pattern: {
                                    value: /^(?:\+?\d{1,4}[-\s]?)?0?\d{1,4}[-\s]?\d{1,4}[-\s]?\d{4}$/,
                                    message: '※電話番号形式が正しくありません'
                                },
                                maxLength: {
                                    value: 20,
                                    message: '※電話番号は20文字以内で入力してください'
                                }
                            })}
                            error={!!errors.client_contact_phone}
                            helperText={errors.client_contact_phone?.message}
                        />

                        <TextField
                            label="担当者EMAIL"
                            disabled={!isEditMode}
                            inputProps={{ maxLength: 255 }}
                            {...register('client_contact_email', {
                                required: mode !== 'estimate' ? '※担当者EMAILは必須です' : false,
                                maxLength: {
                                    value: 255,
                                    message: '※EMAILは255文字以内で入力してください'
                                },
                                pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: '※正しいメールアドレス形式で入力してください'
                                }
                            })}
                            error={!!errors.client_contact_email}
                            helperText={errors.client_contact_email?.message}
                            multiline
                        />
                    </>
                )}
                {/*
                <TextField
                    label="支払い条件"
                    disabled={!isEditMode}
                    inputProps={{ maxLength: 255 }}
                    {...register('payment_terms', {
                        maxLength: {
                            value: 255,
                            message: '※支払い条件は255文字以内で入力してください'
                        }
                    })}
                    error={!!errors.payment_terms}
                    helperText={errors.payment_terms?.message}
                />
                 */}
                {mode === 'invoice' && (
                    <Controller
                        name="payment_terms"
                        control={control}
                        rules={{
                            maxLength: {
                                value: 255,
                                message: '※支払い条件は255文字以内で入力してください'
                            }
                        }}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                freeSolo
                                options={billingRuleOptions}
                                value={field.value || null}
                                onChange={(e, newValue) => {
                                    field.onChange(newValue || '');
                                }}
                                onInputChange={(e, value, reason) => {
                                    if (reason === 'input') {
                                        field.onChange(value);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="支払い条件"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        inputProps={{
                                            ...params.inputProps,
                                            maxLength: 255
                                        }}
                                    />
                                )}
                                disabled={!isEditMode}
                            />
                        )}
                    />
                )}
                {mode === 'invoice' && (
                    <Controller
                        name="payment_method_name"
                        control={control}
                        rules={{
                            maxLength: {
                                value: 100,
                                message: '※100文字以内で入力してください'
                            }
                        }}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                {...field}
                                freeSolo
                                options={paymentOptions}
                                getOptionLabel={(option) => option.payment_method_name || option}
                                value={
                                    paymentOptions.find(
                                        p => p.payment_method_name === field.value
                                    ) || field.value || null
                                }
                                onChange={(e, newValue) => {
                                    const value = typeof newValue === 'string'
                                        ? newValue
                                        : newValue?.payment_method_name ?? '';

                                    field.onChange(value);

                                    if (typeof newValue === 'string') {
                                        // 自由入力の場合はIDはクリア
                                        setValue('payment_method_id', null);
                                    } else if (newValue) {
                                        // 候補選択時はIDもセット
                                        setValue('payment_method_id', newValue.payment_method_id ?? null);

                                    }
                                }}
                                onInputChange={(e, value, reason) => {
                                    if (reason === 'input') {
                                        field.onChange(value);
                                        setValue('payment_method_id', null);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="支払方法"
                                        inputProps={{
                                            ...params.inputProps,
                                            maxLength: 100
                                        }}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                                disabled={!isEditMode}
                            />
                        )}
                    />
                )}
                {mode === 'estimate' && (
                    <TextField
                        label="有効期限"
                        type="date"
                        name="valid_until"
                        disabled={!isEditMode}
                        error={!!errors.valid_until}
                        helperText={errors.valid_until?.message}
                        {...register('valid_until',
                        )}
                    />
                )}
                {mode === 'invoice' && (
                    <TextField
                        label="支払期日"
                        type="date"
                        name="payment_due_date"
                        disabled={!isEditMode}
                        error={!!errors.payment_due_date}
                        helperText={errors.payment_due_date?.message}
                        {...register('payment_due_date',
                        )}
                    />
                )}
            </Box >
            <ClientModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSelectClient={handleCustomerSelect}
            />
        </>
    );
};
export default OrderClientInfo;